/**
 * League › Trades — the archive, plus whatever is currently in flight.
 *
 * Built from `handoff/design_handoff_faab_trades/FFL Pass 07 Trades.dc.html`.
 *
 * THE PAGE IS NOT A NEGOTIATION. Terms are agreed in Discord, as they have been for eleven years,
 * and 2015-II-2 keeps the counter-offers private conversations. What arrives here is the record
 * and the clock: a trade is either inside its counter window and read-only, or it is history.
 * Everything on the page follows from that — there is no "propose", no chat, no per-trade page.
 *
 * ONE FILING RULE, EVERYWHERE. A trade belongs to the season of the PICKS it moves, not the month
 * it was agreed, which is why the 19 November 2025 deal sits under 2026 next to the draft it
 * changes. It is also why the empty state explains the rule rather than saying "no results": a
 * reader who filtered to 2025 and found nothing has not made a mistake, he has met the convention.
 *
 * THE SEPARATE `2026` CHIP IS DELIBERATELY ABSENT. `In the upcoming draft` is the wider set — it
 * catches a 2024 trade that moved a 2026 pick, and the sixth-round swap clause that moves nothing
 * until John decides. A `2026` chip beside it would show a subset under a name that sounds like
 * the whole, and the league would press the wrong one.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import {
  TRADES, TRADE_SOURCE, allTrades, changesDraft, searchTrades, tradePartners, tradeSeasons,
  type TradeRecord,
} from '../domain/trades.js';
import {
  exerciseDoc, optionHolder, optionRound, ordinal, sayDate, swapPlan,
} from '../domain/tradeOption.js';
import {
  canSeeTrade, stillListed, type DraftClock,
} from '../domain/tradeWindow.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { phaseOf } from '../domain/phase.js';
import { faab as faabAmt } from '../domain/faab.js';
import { GmBadge } from './GmBadge.js';
import { PlayerClubChip, PositionChip } from './chips.js';
import { bareName, positionOf } from './TradeAssets.js';
import { LiveTradeCard } from './TradeLive.js';
import { TradeEntry } from './TradeEntry.js';
import { AddTrade } from './AddTrade.js';
import { Linkify, Rule } from './RuleText.js';
import { note } from './auditor.js';
import { useOwnership } from './useOwnership.js';
import { gmOfTeam } from '../domain/identity.js';
import { toFile } from '../domain/tradeFiling.js';
import { settlePlan } from '../domain/tradeSettle.js';
import { chargedNow, pricedSlate, type PricedRow } from '../domain/tradeKeepers.js';
import { ownedRoundsFor } from '../domain/picks2026.js';
import keeperRows from '../data/keepers-2026.json';

const ROWS = keeperRows as unknown as PricedRow[];
import {
  atLeast, type DraftState, type KeeperDeclaration, type LeagueSettings, type LeagueStore,
  type LiveTradeDoc, type Role, type Trade,
} from '../store/types.js';

const SEASON = 2026;

type Chip = { key: string; label: string };
type View = 'log' | 'partners';

export function Trades(
  { gm, actor, role, store }:
  { gm: string; actor: string; role: Role; store: LeagueStore },
) {
  const isCommish = atLeast(role, 'commissioner');
  const [view, setView] = useState<View>('log');

  /*
   * The draft date is read HERE rather than passed in, because it is not chrome — it decides
   * which of the three windows governs (`domain/tradeWindow.ts`), and a page that gets it wrong
   * shows the wrong countdown on a live deal. `phaseOf` gives the in-season half of the same
   * question from the same document, so the two can never disagree about what time of year it is.
   */
  const [season, setSeason] = useState<LeagueSettings | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => void store.settings(SEASON).then((s) => { if (alive) setSeason(s); });
    load();
    const off = store.subscribe(SEASON, (w) => { if (w === 'settings') load(); });
    return () => { alive = false; off(); };
  }, [store]);
  const draftDate = season?.draftDate ?? null;
  const inSeason = phaseOf(season) === 'inseason';

  /*
   * THE BOARD, because on draft day the trade window is measured in picks and not in hours
   * (2017-II-3 §2). Without it a draft-day trade cannot say when it closes, when it had to be
   * announced by, or whether it is still legal — so the page reads the same draft state the board
   * itself runs on rather than a copy.
   */
  const [board, setBoard] = useState<DraftState | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => void store.draftState(SEASON).then((d) => { if (alive) setBoard(d); });
    load();
    const off = store.subscribe(SEASON, (w) => { if (w === 'draftState') load(); });
    return () => { alive = false; off(); };
  }, [store]);

  const clock: DraftClock | undefined = board?.order?.length
    ? {
      // `onTheClock` only means anything once the board is live; before that nothing is selected.
      onTheClock: board.status === 'setup' ? null : board.onTheClock,
      order: board.order,
      teams: LEAGUE_2026.teams,
      startsAt: draftDate,
    }
    : undefined;
  const ctx = { draftDate, inSeason, ...(clock ? { clock } : {}) };
  const { ownership } = useOwnership(store);

  /**
   * SUBMITTED SLATES, for the affirmation card's keeper chooser. A GM about to affirm a trade
   * that takes a keeper's pick is entitled to say which keeper moves (2015-III-7), and the card
   * cannot ask him without knowing what he has declared.
   */
  const [decls, setDecls] = useState<KeeperDeclaration[]>([]);
  useEffect(() => {
    let alive = true;
    const load = () => void store.declarations(SEASON).then((d) => {
      if (alive) setDecls(d.filter((x) => x.status === 'submitted'));
    });
    load();
    const off = store.subscribe(SEASON, (w) => { if (w === 'declarations') load(); });
    return () => { alive = false; off(); };
  }, [store]);
  const slateOf = (gm: string) => {
    const who = gm.toUpperCase();
    const decl = decls.find((d) => d.gm.toUpperCase() === who);
    const keepers = pricedSlate(who, decl, ROWS, gmOfTeam);
    const ownedRounds = ownedRoundsFor(who, ownership, LEAGUE_2026.draftRounds);
    return { decl, keepers, ownedRounds, now: chargedNow(keepers, decl, ownedRounds) };
  };

  /* -- the archive ------------------------------------------------------------------------- */
  const [added, setAdded] = useState<Trade[]>([]);
  const [live, setLive] = useState<LiveTradeDoc[]>([]);
  useEffect(() => {
    let alive = true;
    const load = () => {
      void store.tradesAll().then((t) => { if (alive) setAdded(t); });
      // A store built before trades had a live stage simply has no such method; the page loses
      // the in-flight block and keeps the archive rather than failing to render at all.
      void store.liveTrades?.(SEASON).then((t) => { if (alive) setLive(t); }).catch(() => {});
    };
    load();
    const off = store.subscribe(SEASON, (w) => {
      if (w === 'trades' || w === 'liveTrades') load();
    });
    return () => { alive = false; off(); };
  }, [store]);

  const all = useMemo(() => allTrades(added), [added]);
  const seasons = useMemo(() => tradeSeasons(all), [all]);
  const partners = useMemo(() => tradePartners(all), [all]);

  const [chip, setChip] = useState<string>('all');
  const [who, setWho] = useState<string>('all');
  const [q, setQ] = useState('');

  const chips: Chip[] = [
    { key: 'all', label: 'Every season' },
    { key: 'draft', label: 'In the upcoming draft' },
    ...seasons.map((s) => ({ key: String(s), label: String(s) })),
  ];

  const shown = useMemo(() => {
    const base = searchTrades({ season: 'all', gm: who, q }, all);
    if (chip === 'all') return base;
    if (chip === 'draft') return base.filter((t) => changesDraft(t, SEASON));
    return base.filter((t) => t.season === Number(chip));
  }, [all, who, q, chip]);

  /* -- what is in flight ------------------------------------------------------------------- */
  const visible = useMemo(
    () => live
      .filter((t) => canSeeTrade(t, gm, isCommish))
      // Which ended trades still deserve a card, and why, is `stillListed`.
      .filter((t) => stillListed(t, ctx)),
    [live, gm, isCommish, draftDate, inSeason, clock?.onTheClock, clock?.order],
  );

  const saveLive = (t: LiveTradeDoc) => void store.saveLiveTrade?.(t);

  /**
   * File a closed trade into the 109-row ledger.
   *
   * The arithmetic — the row, the stamp, and the keeper reallocation 2015-III-7 forces — is
   * `domain/tradeSettle.ts`, shared with the scheduled function that files what no session was
   * open for. This performs the plan's writes in its order: the ledger row first, then the live
   * record stamped with its id. If the second fails the trade stays on the page as "waiting to be
   * filed" and pressing the button again writes the SAME ledger id — `settlementFor` derives it
   * from the live id — so the retry corrects rather than duplicates. The other order would strike
   * the trade off the page and leave nothing in the log.
   */
  const settle = async (t: LiveTradeDoc) => {
    const [decls, trades, money] = await Promise.all([
      store.declarations(SEASON), store.liveTrades(SEASON), store.faab(SEASON),
    ]);
    const plan = settlePlan({
      trade: t, declarations: decls, liveTrades: trades, faabLedger: money, rows: ROWS, gmOfTeam,
      draftRounds: LEAGUE_2026.draftRounds, actor, at: new Date().toISOString(),
    });
    await store.saveTrade(plan.ledger);
    /*
     * The money before the stamp. `plan.faab` is the GAP between what this trade says and what the
     * ledger holds for it, so a retry after a part-way failure writes what is still missing and
     * nothing else — see `domain/tradeFaab.ts`.
     */
    for (const e of plan.faab) await store.saveFaab(e);
    /* The lease comes off with the same write that records the id; see `domain/tradeFiling.ts`. */
    await store.saveLiveTrade?.(plan.live);
    for (const d of plan.declarations) await store.saveDeclaration(d);
    for (const n of plan.notes) void store.recordAudit?.(n).catch(() => {});
  };

  /**
   * A CLOSED WINDOW FILES ITSELF (Tim, 3 September).
   *
   * There is no judgement left in a window that has run its course: nobody countered it out and
   * nobody vetoed it, so it stands. Waiting for a commissioner to press a button was never a rule,
   * it was the shape of the code, and the footer copy said so in a way that read like one.
   *
   * The act is still performed under a commissioner's credentials because `firestore.rules` allows
   * nobody else to write the ledger. So this runs in whichever commissioner's session is open, and
   * it is the same `settle` the button calls — one path, so the automatic case cannot drift from
   * the manual one. `toFile` decides what is due and whether somebody else is already on it.
   *
   * ONE AT A TIME, AND ONCE PER SESSION. Serially because `reallocateKeepers` reads every
   * declaration and writes some of them, so two settles overlapping would fit a slate against a
   * board that is half-written. Once because a settle that throws must not be retried on every
   * render against whatever refused it; the button underneath the card is the retry.
   */
  const attempted = useRef<Set<string>>(new Set());
  const filing = useRef(false);
  useEffect(() => {
    if (!isCommish || filing.current) return;
    const due = toFile(live, ctx, actor, attempted.current);
    if (due.length === 0) return;
    filing.current = true;
    void (async () => {
      try {
        for (const t of due) {
          attempted.current.add(t.id);
          const held: LiveTradeDoc = {
            ...t,
            settling: { by: actor.toUpperCase(), at: new Date().toISOString() },
          };
          await store.saveLiveTrade?.(held);
          await settle(held);
        }
      } catch {
        /* Left on the page as "waiting to be filed", with the button that was always there. */
      } finally {
        filing.current = false;
      }
    })();
  }, [live, isCommish, actor, draftDate, inSeason, clock?.onTheClock, clock?.order]);

  /*
   * CORRECTING THE ARCHIVE IS A DIFFERENT ACT FROM FILING A TRADE, and it kept its own form.
   *
   * The entry card above puts a deal into the WINDOW — it is going to happen, and somebody has to
   * affirm it. This fixes a row in eleven years of history that was typed wrong, and it is
   * commissioner-only, one row at a time, and reached from the row itself rather than from a
   * standing form at the top of the page. Merging the two would mean one form whose Send button
   * sometimes starts a 48-hour clock and sometimes silently rewrites 2017.
   *
   * Striking is two acts wearing one word. A trade the app recorded is deleted; a BUNDLED one
   * cannot be, because 109 of them ship inside the build — so a tombstone is written and the
   * reader drops it. That is also what makes a strike reversible.
   */
  const [editing, setEditing] = useState<string | null>(null);
  /**
   * A ledger row in the store's shape. The stored copy where there is one — it carries fields
   * the read-side record flattens — and otherwise the bundled row lifted into a `Trade`, which
   * is how a correction to one of the 109 is written: same id, database wins.
   */
  const toTrade = (id: string): Trade | null => {
    const stored = added.find((x) => x.id === id);
    if (stored) return stored;
    const row = all.find((x) => x.id === id);
    if (!row) return null;
    return {
      id: row.id,
      season: row.season ?? SEASON,
      date: row.date ?? '',
      sides: [0, 1].map((i) => ({
        gm: row.sides[i]!.gm,
        players: [...row.sides[i]!.players],
        rights: [...row.sides[i]!.rights],
        picks: row.sides[i]!.picks.map((pk) => pk.raw),
        faab: row.sides[i]!.faab ?? 0,
      })) as Trade['sides'],
      ...(row.notes?.length ? { note: row.notes.join(' · ') } : {}),
      ...(row.clauseOutcome ? { clauseOutcome: row.clauseOutcome } : {}),
      recordedBy: actor,
      recordedAt: new Date().toISOString(),
    };
  };
  const editTrade = useMemo((): Trade | null => (editing ? toTrade(editing) : null),
    [editing, added, all, actor]);  // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * AN OPTION, USED. See `domain/tradeOption.ts`. The exercise is filed as a trade and settled at
   * once — ledger row, ownership, keepers — and the row the option came out of is stamped with
   * the answer so the band can say "exercised" and stop offering the buttons. Declining stamps
   * the row and moves nothing.
   */
  const stampOutcome = async (row: TradeRecord, outcome: 'exercised' | 'declined', by: string, tradeId?: string) => {
    const base = toTrade(row.id);
    if (!base) return;
    await store.saveTrade({
      ...base,
      clauseOutcome: {
        outcome, by: by.toUpperCase(), at: new Date().toISOString(),
        ...(tradeId ? { tradeId } : {}), recordedBy: actor,
      },
      recordedBy: actor,
      recordedAt: new Date().toISOString(),
    });
  };
  const exerciseOption = async (row: TradeRecord, by: string, round: number): Promise<string | null> => {
    const other = row.sides.find((x) => x.gm.toUpperCase() !== by.toUpperCase());
    if (!other) return 'That manager is not on this trade.';
    const plan = swapPlan(by, other.gm, round, SEASON, ownership, clock?.order ?? [], LEAGUE_2026.teams, managerShort);
    if (!plan.ok || !plan.sides) return plan.why ?? 'That swap cannot be made.';
    const at = new Date().toISOString();
    const doc = exerciseDoc(row, plan.sides, round, SEASON, actor, at, managerShort);
    await settle(doc);
    await stampOutcome(row, 'exercised', by, `live-${doc.id}`);
    note(store, actor, {
      kind: 'note', onBehalfOf: by.toUpperCase(), target: `trades/${row.id}`,
      what: `${managerShort(by)} exercised the ${ordinal(round)}-round swap from the ${sayDate(row.date)} `
        + `trade: ${managerShort(by)} takes ${managerShort(other.gm)}’s ${plan.gives?.[other.gm.toUpperCase()]}, `
        + `${managerShort(other.gm)} takes ${managerShort(by)}’s ${plan.gives?.[by.toUpperCase()]}`,
    });
    return null;
  };
  const declineOption = async (row: TradeRecord, by: string) => {
    await stampOutcome(row, 'declined', by);
    note(store, actor, {
      kind: 'note', onBehalfOf: by.toUpperCase(), target: `trades/${row.id}`,
      what: `${managerShort(by)} declined the option in the ${sayDate(row.date)} trade — nothing moves`,
    });
  };

  async function strike(id: string) {
    const stored = added.find((x) => x.id === id);
    if (stored && !stored.voided) {
      // A deleted row leaves no evidence behind, so the note is the only record it happened.
      note(store, actor, {
        kind: 'delete',
        what: `Deleted a trade recorded in the app — ${stored.sides.map((x) => x.gm).join(' and ')}`,
        target: `trades/${id}`,
      });
      await store.deleteTrade(stored.season ?? SEASON, id);
      return;
    }
    const row = all.find((x) => x.id === id);
    if (!row) return;
    await store.saveTrade({
      id,
      season: row.season ?? SEASON,
      date: row.date ?? '',
      sides: [
        { gm: row.sides[0]!.gm, players: [], rights: [], picks: [], faab: 0 },
        { gm: row.sides[1]!.gm, players: [], rights: [], picks: [], faab: 0 },
      ],
      voided: true,
      recordedBy: actor,
      recordedAt: new Date().toISOString(),
    });
  }

  return (
    <div className="app wide trades">
      <Linkify>
        <header className="pagehead trhead">
          <div>
            <h1>Trades and Trade Log <Rule id="2022-trade-declarations" /></h1>
            <p className="sub">
              {all.length} recorded, {seasons.length} seasons.
            </p>
          </div>
          <div className="trtools">
            <div className="searchwrap">
              <input className="search" type="search" placeholder="Player, pick or clause"
                value={q} onChange={(e) => setQ(e.target.value)} />
              {q && (
                <button type="button" className="searchclear" aria-label="Clear the search"
                  onClick={() => setQ('')}>×</button>
              )}
            </div>
            <select value={who} onChange={(e) => setWho(e.target.value)} aria-label="Filter by GM">
              <option value="all">Every GM</option>
              {partners.gms.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
            </select>
            <div className="panes small">
              <button type="button" className={`pane ${view === 'log' ? 'on' : ''}`}
                onClick={() => setView('log')}>Log</button>
              {/*
                Partner counts came off History when its Trades tab was deleted. They are a
                SECONDARY view here rather than a section of their own: "who deals with whom" is
                a question about this data, and the alternative was leaving a one-table page in a
                menu it no longer belonged to.
              */}
              <button type="button" className={`pane ${view === 'partners' ? 'on' : ''}`}
                onClick={() => setView('partners')}>Partners</button>
            </div>
          </div>
        </header>

        {view === 'partners' ? (
          <Partners partners={partners} seasons={seasons} />
        ) : (
          <>
            <TradeEntry gm={gm} role={role} ctx={ctx} store={store}
              keeperDeadline={season?.keeperDeadline ?? null} onFile={saveLive} />

            {isCommish && editing && (
              <AddTrade actor={actor} store={store} editing={editTrade}
                onCancelEdit={() => setEditing(null)}
                onStrike={() => { if (editing) { void strike(editing); setEditing(null); } }}
                onSaved={() => { setChip('all'); setEditing(null); }} />
            )}

            {visible.map((t) => (
              <LiveTradeCard key={t.id} t={t}
                ctx={{
                  ...ctx, gm, role, ownership, slateOf,
                  keeperDeadline: season?.keeperDeadline ?? null,
                  onSave: saveLive,
                  onSettle: (x) => void settle(x),
                  // The matching tail is defined against the trade a replacement came out of, and
                  // that trade may itself be off the page by then — so the lookup is over
                  // everything loaded, not over what is currently listed.
                  originalOf: (id) => live.find((x) => x.id === id),
                }} />
            ))}

            <div className="trchips">
              <div className="trchiprow">
                {chips.map((c) => (
                  <button type="button" key={c.key} className={`chipbtn ${chip === c.key ? 'on' : ''}`}
                    onClick={() => setChip(c.key)}>{c.label}</button>
                ))}
              </div>
              <span className="mut trcount">
                {shown.length === all.length
                  ? `all ${all.length}`
                  : `${shown.length} of ${all.length}`}
              </span>
            </div>

            {shown.length === 0 ? <Empty chip={chip} /> : (
              <ol className="trfeed">
                {shown.map((t) => (
                  <ArchiveCard key={t.id} t={t} me={gm}
                    onEdit={isCommish ? () => setEditing(t.id) : undefined}
                    option={isCommish && t.season === SEASON && t.notes?.length && !t.clauseOutcome
                      ? {
                        order: clock?.order ?? [],
                        keepersOn: (who, round) => [...slateOf(who).now]
                          .filter(([, r]) => r === round).map(([p]) => p),
                        onExercise: (by, round) => exerciseOption(t, by, round),
                        onDecline: (by) => declineOption(t, by),
                      }
                      : undefined} />
                ))}
              </ol>
            )}

            <p className="note src">
              {TRADE_SOURCE}
              {actor !== gm && ` · viewing as ${managerShort(gm)}`}
            </p>
          </>
        )}
      </Linkify>
    </div>
  );
}

/* -- one archived trade ------------------------------------------------------------------------ */

/** The commissioner's handle on an option still open in the row's notes. */
interface OptionCtl {
  order: readonly string[];
  /** Keepers this GM has charged to the round, so the panel can say who will be re-charged. */
  keepersOn: (gm: string, round: number) => string[];
  /** Null on success, otherwise the reason it was refused. */
  onExercise: (by: string, round: number) => Promise<string | null>;
  onDecline: (by: string) => Promise<void>;
}

function ArchiveCard(
  { t, me, onEdit, option }:
  { t: TradeRecord; me: string; onEdit?: () => void; option?: OptionCtl },
) {
  const [a, b] = t.sides;
  /*
   * ONE WORD ON THE ROW. "correct" and "strike" were two links on every one of 109 cards, and the
   * second is the destructive one. Tim, 3 September: "correct and strike can be simplified to
   * simply 'edit'." The editor is where the strike now lives, behind its own confirmation.
   */
  return (
    <li className={`trcard ${t.sides.some((s) => s.gm === me) ? 'mine' : ''}`}>
      <div className="trcardhead">
        <span className="trdate">{t.date ?? 'no date in the sheet'}</span>
        <span className="trseason">{t.season ?? '—'} picks</span>
        <span className="trpartners">
          {managerShort(a!.gm)} and {managerShort(b!.gm)}
        </span>
        <span className="spacer" />
        {t.addedBy && (
          <span className="chip warning tiny">
            {TRADES.some((x) => x.id === t.id) ? 'corrected here' : 'added here'}
          </span>
        )}
        {onEdit && <button type="button" className="linkbtn tiny" onClick={onEdit}>edit</button>}
      </div>
      {/*
        THE STORED SIDE IS WHAT THAT MAN GOT, not what he sent.
        `sides[0]` of the 19 November deal is RAJAN with Jefferson and McLaurin on it, and Rajan is
        the man who received them. The live-trade record on the same page is the other way round —
        `LiveTradeSide.sends` — because a man composing a trade is choosing what to give up. The two
        conventions are both defensible and this card sat between them: it paired each GM with the
        OTHER side's contents and printed the 109-row archive backwards, giving John's receivers to
        Rajan and Rajan's to John. `settlementFor` is the one place the swap is now performed.
      */}
      <div className="trgets">
        <Got side={a!} gm={a!.gm} />
        <Got side={b!} gm={b!.gm} />
      </div>
      {/*
        The clause band, amber, one line. It is the only part of a trade that is not an asset, and
        the band exists so that it cannot be read as one — 109 rows of two-column assets with a
        sentence quietly sitting in one of the columns is how the swap right became a pick.
      */}
      {/*
        The sheet prefixes some notes with the GM they belong to — "JOHN: right to swap 6th rd
        picks". The band already says "Other consideration", and the clause already names John in
        its own words, so the prefix is dropped rather than printed twice.
      */}
      {t.notes?.map((n) => (
        <p className="trclauseband" key={n}>
          Other consideration: {n.replace(/^[A-Z]{2,8}:\s*/, '')}
        </p>
      ))}
      {t.clauseOutcome && (
        <p className="trclauseband done">
          {t.clauseOutcome.outcome === 'exercised' ? 'Exercised' : 'Declined'} by{' '}
          {managerShort(t.clauseOutcome.by)}, {sayDate(t.clauseOutcome.at.slice(0, 10))}
          {t.clauseOutcome.outcome === 'exercised' && ' — the swap is filed as its own row above.'}
        </p>
      )}
      {option && <OptionPanel t={t} ctl={option} />}
    </li>
  );
}

/**
 * The commissioner's two answers to an open option: use it, or let it lapse. Reached from the
 * row the clause sits on, because the clause is the only thing on the page that knows it exists.
 */
function OptionPanel({ t, ctl }: { t: TradeRecord; ctl: OptionCtl }) {
  const gms = t.sides.map((s) => s.gm.toUpperCase());
  const [by, setBy] = useState<string>(() => optionHolder(t, managerShort) ?? '');
  const [round, setRound] = useState<number>(() => optionRound(t) ?? 0);
  const [mode, setMode] = useState<'idle' | 'exercise' | 'decline'>('idle');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const other = gms.find((g) => g !== by) ?? '';
  const named = Boolean(by) && round > 0;
  const slot = (gm: string) => {
    const i = ctl.order.findIndex((g) => g.toUpperCase() === gm);
    return i < 0 ? null : i + 1;
  };
  const code = (gm: string) => {
    const s = slot(gm);
    return s === null ? ordinal(round) : `${round}.${String(round % 2 === 1 ? s : LEAGUE_2026.teams - s + 1).padStart(2, '0')}`;
  };
  const moved = named
    ? [by, other].flatMap((g) => ctl.keepersOn(g, round).map((p) => `${p} (${managerShort(g)})`))
    : [];
  const run = async (f: () => Promise<string | null | void>) => {
    setBusy(true);
    setProblem(null);
    try {
      const why = await f();
      if (why) setProblem(why);
      else setMode('idle');
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="troption">
      {mode === 'idle' && (
        <>
          <span className="mut">This clause is an option still open.</span>
          <button type="button" className="linkbtn tiny" onClick={() => setMode('exercise')}>exercise it</button>
          <button type="button" className="linkbtn tiny" onClick={() => setMode('decline')}>decline it</button>
        </>
      )}
      {mode !== 'idle' && (
        <>
          <label className="troptfield">
            <span>Whose option</span>
            <select value={by} onChange={(e) => setBy(e.target.value)}>
              {!by && <option value="">— who? —</option>}
              {gms.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
            </select>
          </label>
          {mode === 'exercise' && (
            <label className="troptfield">
              <span>Swap picks in round</span>
              <input type="number" min={1} max={LEAGUE_2026.draftRounds} value={round || ''}
                onChange={(e) => setRound(Number(e.target.value))} />
            </label>
          )}
          {mode === 'exercise' && named && (
            <span className="troptsay">
              {managerShort(by)} takes {managerShort(other)}’s {code(other)}; {managerShort(other)} takes{' '}
              {managerShort(by)}’s {code(by)}. Filed as a trade dated today, settled at once — no window,
              the league already had its 48 hours on the deal this came out of.
              {moved.length > 0 && (
                <> Standing on those picks: {moved.join(', ')} — re-charged to an earlier pick when
                  this is recorded (2015-III-7).</>
              )}
            </span>
          )}
          {mode === 'decline' && by && (
            <span className="troptsay">
              {managerShort(by)} lets the option lapse. Nothing moves; the row is marked so it is not
              offered again.
            </span>
          )}
          {problem && <span className="chip critical tiny">{problem}</span>}
          <span className="troptbtns">
            <button type="button" className="btn small" disabled={busy} onClick={() => { setMode('idle'); setProblem(null); }}>
              Cancel
            </button>
            {mode === 'exercise' ? (
              <button type="button" className="btn small primary" disabled={busy || !named}
                onClick={() => void run(() => ctl.onExercise(by, round))}>
                {busy ? 'Recording…' : 'Record the swap'}
              </button>
            ) : (
              <button type="button" className="btn small primary" disabled={busy || !by}
                onClick={() => void run(() => ctl.onDecline(by))}>
                {busy ? 'Recording…' : 'Record the decline'}
              </button>
            )}
          </span>
        </>
      )}
    </div>
  );
}

/** What one manager GOT — which the ledger stores on his OWN side. See the note on the card. */
function Got({ side, gm }: { side: TradeRecord['sides'][number]; gm: string }) {
  const empty = side.players.length === 0 && side.rights.length === 0
    && side.picks.length === 0 && !side.faab;
  return (
    <div className="trgetcol">
      <GmBadge gm={gm} />
      <div className="trgetbody">
        <span className="trgetlab">{managerShort(gm)} got</span>
        {empty && <span className="mut">nothing recorded</span>}
        {side.players.map((p) => (
          <span className="trline" key={p}>
            <PositionChip pos={positionOf(p)} />
            <PlayerClubChip player={bareName(p)} />
            <span className="trname">{bareName(p)}</span>
          </span>
        ))}
        {side.rights.map((r) => (
          <span className="trline rights" key={r}>
            <span className="trkind">rights</span><span className="trname">{r}</span>
          </span>
        ))}
        {side.picks.map((p) => (
          <span className={`trline pick ${p.farm ? 'farm' : ''}`} key={p.raw}>
            <span className="trpick">{p.raw}</span>
          </span>
        ))}
        {side.faab > 0 && (
          <span className="trline faab"><span className="trmoney">{faabAmt(side.faab)}</span></span>
        )}
      </div>
    </div>
  );
}

/**
 * The empty state explains the filing rule.
 *
 * "No results" would be a lie of omission here: the commonest way to see this screen is to filter
 * to the year a trade was AGREED and find it filed under the year of the picks it moved.
 */
function Empty({ chip }: { chip: string }) {
  return (
    <div className="trempty">
      <p className="tremptytitle">Nothing filed under that.</p>
      <p className="mut">
        A trade files under the season of the picks it moves, not the month it was agreed — the
        19 November 2025 deal is a 2026 trade, because 2026 picks crossed in it.
        {chip !== 'all' && ' Try Every season, or In the upcoming draft.'}
      </p>
    </div>
  );
}

/* -- who deals with whom ----------------------------------------------------------------------- */

function Partners(
  { partners, seasons }:
  { partners: ReturnType<typeof tradePartners>; seasons: readonly number[] },
) {
  return (
    <section className="card">
      <div className="striptop">
        <h3 className="striphead">Who deals with whom</h3>
        <span className="mut coverage">{seasons.at(-1)}–{seasons[0]}</span>
      </div>
      <p className="note">
        Trades between each pair, counted once each. The row total is every trade that manager has
        made.
      </p>
      <div className="gridwrap">
        <table className="h2h">
          <thead>
            <tr>
              <th />
              {partners.gms.map((g) => <th key={g}><GmBadge gm={g} /></th>)}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {partners.gms.map((a) => (
              <tr key={a}>
                <th><GmBadge gm={a} withName /></th>
                {partners.gms.map((b) => {
                  const n = partners.grid.get(a)?.get(b) ?? 0;
                  if (a === b) return <td key={b} className="self" />;
                  return (
                    <td key={b} className={`h2hcell ${n >= 6 ? 'up' : n === 0 ? 'zero' : ''}`}
                      title={`${managerShort(a)} and ${managerShort(b)} have made ${n} trade${n === 1 ? '' : 's'}`}>
                      <span className="rec">{n || '·'}</span>
                    </td>
                  );
                })}
                <td className="num strong">{partners.totals.get(a) ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
